import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { Heart, Trash2, Sparkles } from "lucide-react";

const EMPTY_BG =
  "https://static.prod-images.emergentagent.com/jobs/e47687e4-2c30-4839-af89-7a84ff5b6888/images/8d8b0842f24c08552ef392c42235d87d2a6bdd814c9f03e31c3192f31f71ad05.png";

export default function Library() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all"); // all | favorites
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/prayers");
      setItems(data);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleFav = async (e, p) => {
    e.stopPropagation();
    const { data } = await api.post(`/prayers/${p.id}/favorite`);
    setItems((arr) => arr.map((x) => (x.id === p.id ? { ...x, favorite: data.favorite } : x)));
  };

  const remove = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm("Release this prayer from your library?")) return;
    await api.delete(`/prayers/${p.id}`);
    setItems((arr) => arr.filter((x) => x.id !== p.id));
  };

  const open = (p) => {
    navigate("/prayer", { state: { prayer: { ...p } } });
  };

  const visible = (items || []).filter((p) => (filter === "favorites" ? p.favorite : true));

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--sanctuary-bg)" }}>
      <Header />
      <div className="sanctuary-glow absolute inset-x-0 top-16 h-[420px] -z-0 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase mb-3" style={{ color: "var(--sanctuary-gold)" }}>
              Your sanctuary
            </p>
            <h1
              className="font-serif-display text-4xl md:text-5xl tracking-tight"
              style={{ color: "var(--sanctuary-ink)" }}
              data-testid="library-title"
            >
              Library of prayers
            </h1>
          </div>
          <div className="flex items-center gap-2" data-testid="library-filters">
            {["all", "favorites"].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`library-filter-${k}`}
                className="px-4 py-2 rounded-full text-sm transition"
                style={{
                  backgroundColor: filter === k ? "var(--sanctuary-ink)" : "transparent",
                  color: filter === k ? "var(--sanctuary-bg)" : "var(--sanctuary-ink-soft)",
                  border: filter === k ? "1px solid #2D2C2A" : "1px solid #E8E4D9",
                }}
              >
                {k === "all" ? "All" : "Favorites"}
              </button>
            ))}
          </div>
        </div>

        {items === null && (
          <p data-testid="library-loading" className="font-serif-display italic" style={{ color: "var(--sanctuary-ink-muted)" }}>
            Gathering…
          </p>
        )}

        {items !== null && visible.length === 0 && (
          <div className="text-center py-16" data-testid="library-empty">
            <img
              src={EMPTY_BG}
              alt=""
              className="w-40 h-40 object-cover rounded-full mx-auto mb-8 opacity-80"
              style={{ filter: "saturate(0.9)" }}
            />
            <h3 className="font-serif-display text-2xl mb-3" style={{ color: "var(--sanctuary-ink)" }}>
              {filter === "favorites" ? "No favorites yet" : "Your library waits in stillness"}
            </h3>
            <p className="mb-8 text-base max-w-md mx-auto" style={{ color: "var(--sanctuary-ink-soft)" }}>
              When you save a prayer, it lives here for you to return to whenever you need its words.
            </p>
            <Link
              to="/builder"
              data-testid="library-empty-cta"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full btn-primary text-base"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              Compose a prayer
            </Link>
          </div>
        )}

        {items !== null && visible.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-testid="library-grid">
            {visible.map((p) => (
              <button
                key={p.id}
                onClick={() => open(p)}
                data-testid={`library-item-${p.id}`}
                className="text-left p-7 md:p-9 rounded-3xl transition hover:-translate-y-0.5"
                style={{
                  backgroundColor: "var(--sanctuary-veil-2)",
                  boxShadow: "0 8px 32px rgba(45,44,42,0.04)",
                  border: "1px solid rgba(232,228,217,0.6)",
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="font-serif-display text-2xl tracking-tight leading-tight" style={{ color: "var(--sanctuary-ink)" }}>
                    {p.title}
                  </h3>
                  <span
                    onClick={(e) => toggleFav(e, p)}
                    data-testid={`library-fav-${p.id}`}
                    className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition hover:bg-white"
                    role="button"
                  >
                    <Heart
                      className="w-4 h-4"
                      strokeWidth={1.5}
                      fill={p.favorite ? "var(--sanctuary-accent)" : "none"}
                      color={p.favorite ? "var(--sanctuary-accent)" : "var(--sanctuary-ink-muted)"}
                    />
                  </span>
                </div>
                <p
                  className="text-base leading-relaxed line-clamp-4 whitespace-pre-wrap"
                  style={{ color: "var(--sanctuary-ink-soft)" }}
                >
                  {p.body}
                </p>
                <div className="mt-6 flex items-center justify-between text-xs" style={{ color: "var(--sanctuary-ink-muted)" }}>
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                  <span
                    onClick={(e) => remove(e, p)}
                    data-testid={`library-delete-${p.id}`}
                    className="inline-flex items-center gap-1 hover:opacity-70 transition"
                    role="button"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    Release
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
